import React, { useCallback, useEffect, useRef, useState } from "react";
import { setRandomBackground } from "../helpers/backgroundHelpers";
import { ChatMessage } from "../models/chatMessage";
import { generateUUID } from "three/src/math/MathUtils";

const CHAT_SERVER_URL = "wss://robyn-chat-api.azurewebsites.net/";
const MAX_RECONNECT_DELAY_MS = 15000;

type ConnectionStatus = "connecting" | "connected" | "disconnected";

let storedUserId: string | null = null;
if (typeof window !== "undefined") {
  storedUserId = localStorage.getItem("chatUserId");
  if (!storedUserId) {
    storedUserId = generateUUID();
    localStorage.setItem("chatUserId", storedUserId);
  }
}

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [status, setStatus] = useState<ConnectionStatus>("connecting");
  const [userId] = useState<string>(storedUserId || generateUUID());
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  // Messages sent while disconnected; flushed once the socket opens.
  const outboxRef = useRef<string[]>([]);

  useEffect(() => {
    setRandomBackground();

    let disposed = false;
    let reconnectAttempts = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

    const connect = () => {
      if (disposed) return;
      setStatus("connecting");
      const socket = new WebSocket(CHAT_SERVER_URL);
      socketRef.current = socket;

      socket.onopen = () => {
        reconnectAttempts = 0;
        setStatus("connected");
        const pending = outboxRef.current;
        outboxRef.current = [];
        pending.forEach((payload) => socket.send(payload));
      };

      socket.onmessage = (event) => {
        try {
          const message: ChatMessage = JSON.parse(event.data);
          setMessages((prevMessages) => [...prevMessages, message]);
        } catch (err) {
          console.error("Invalid chat message:", event.data, err);
        }
      };

      socket.onclose = () => {
        if (disposed || socketRef.current !== socket) return;
        setStatus("disconnected");
        const delay = Math.min(1000 * 2 ** reconnectAttempts, MAX_RECONNECT_DELAY_MS);
        reconnectAttempts++;
        reconnectTimer = setTimeout(connect, delay);
      };

      socket.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
    };

    // Reconnect immediately when the tab comes back (e.g. after laptop sleep).
    const handleVisibility = () => {
      const socket = socketRef.current;
      if (
        document.visibilityState === "visible" &&
        (!socket || socket.readyState === WebSocket.CLOSED)
      ) {
        clearTimeout(reconnectTimer);
        reconnectAttempts = 0;
        connect();
      }
    };

    connect();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      disposed = true;
      clearTimeout(reconnectTimer);
      document.removeEventListener("visibilitychange", handleVisibility);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      const message: ChatMessage = {
        type: "chat",
        user: userName.trim() || `anonymous-${userId.substring(0, 8)}`,
        text: trimmed,
        userId,
      };
      const payload = JSON.stringify(message);

      const socket = socketRef.current;
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(payload);
      } else {
        outboxRef.current.push(payload);
      }
    },
    [userName, userId]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(inputText);
    setInputText("");
  };

  return (
    <div
      className="chat-page"
      style={{
        padding: "2rem 1.25rem 4rem",
        maxWidth: 1000,
        margin: "0 auto",
        color: "#fff",
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        lineHeight: 1.45,
      }}
    >
      <header style={{ marginBottom: "2rem" }}>
        <h1 style={{ margin: 0, fontSize: "2.4rem", letterSpacing: "0.5px" }}>
          Chat
        </h1>
        <p style={{ opacity: 0.8, margin: "0.5rem 0 0" }}>
          Chat with others who happen to be here!
        </p>
        {status !== "connected" && (
          <p
            role="status"
            style={{ opacity: 0.6, margin: "0.35rem 0 0", fontSize: "0.9rem" }}
          >
            {status === "connecting" ? "connecting…" : "reconnecting…"}
          </p>
        )}
      </header>

      <section aria-labelledby="chat-messages-heading">
        <h2 id="chat-messages-heading" style={{ fontSize: "1.4rem" }}>
          messages
        </h2>
        <div
          className="chat-messages"
          ref={messagesContainerRef}
          style={{
            marginTop: "1rem",
            maxHeight: "400px",
            overflowY: "auto",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "8px",
            padding: "1rem",
            background: "rgba(0,0,0,0.2)",
          }}
        >
          {messages.length === 0 ? (
            <p style={{ opacity: 0.7 }}>No messages yet.</p>
          ) : (
            messages.map((msg, index) => (
              <div key={index} style={{ marginBottom: "0.75rem" }}>
                <strong>{msg.user || "Anonymous"}:</strong> {msg.text}
                <br />
                <small style={{ opacity: 0.6 }}>
                  {msg.timestamp
                    ? new Date(msg.timestamp).toLocaleTimeString()
                    : ""}
                </small>
              </div>
            ))
          )}
        </div>
      </section>
      <section>
        <h2 style={{ fontSize: "1.4rem", marginTop: "2rem" }}>
          send a message
        </h2>
        <form className="chat-form" onSubmit={handleSubmit}>
          <input
            className="glass-input glass-input-name"
            type="text"
            placeholder="name (optional)"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
          />
          <input
            className="glass-input glass-input-message"
            type="text"
            placeholder="type your message here..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
          />
          <button
            className="chat-send-btn"
            type="submit"
            disabled={!inputText.trim()}
          >
            Send
          </button>
        </form>
      </section>
    </div>
  );
};

export default Chat;
